import { allowedCountries } from '@/data/allowedCountries';

/**
 * Static `{ iso, prefix }` table built once from `allowedCountries`. We don't
 * need the localized display name here — this util only matches dial prefixes.
 */
const PREFIX_TABLE: ReadonlyArray<{ iso: string; prefix: string }> =
  Object.entries(allowedCountries).map(([iso, data]) => ({
    iso,
    prefix: `+${data.dialCode}`,
  }));

/**
 * Detects the country ISO from a value that starts with a dial prefix in
 * either `+XX` or `00XX` form. Prefers the longest matching prefix so that
 * overlapping prefixes (e.g. `+1` vs `+1809`) resolve correctly.
 *
 * Returns `null` if the value doesn't start with an international prefix.
 *
 * Examples:
 *  - `"+43 676 …"`  → `"AT"`
 *  - `"0043 676 …"` → `"AT"`
 *  - `"0676 …"`     → `null` (no international prefix)
 */
const detectIsoFromPrefix = (value: string): string | null => {
  const normalised = value.startsWith('00') ? `+${value.slice(2)}` : value;
  if (!normalised.startsWith('+')) {
    return null;
  }

  let best: { iso: string; prefix: string } | null = null;
  for (const country of PREFIX_TABLE) {
    const matches = normalised.startsWith(country.prefix);
    const isLonger =
      best === null || country.prefix.length > best.prefix.length;
    if (matches && isLonger) {
      best = country;
    }
  }
  return best?.iso ?? null;
};

export default detectIsoFromPrefix;
